<script lang="ts">
  // Libs
  import { slide } from "svelte/transition";
  import { cubicInOut } from "svelte/easing";
  import { startCase, toLower } from "lodash";

  // Services
  import { stagedRolloutCheck } from "@shared/svelte/Modules/StagedRolloutCheck.svelte";
  import { splitByWarehouse, filterByWarehouse } from "@retailer/services/warehouse_service";
  import {
    getContainerCountString,
    getOrderContainerCounts,
  } from "@retailer/services/order_quantities_service";

  // Components
  import CartProductsContainer from "@retailer/svelte/Cart/Page/DistributorSection/CartProductsContainer.svelte";
  import IndividualOrderSummary from "@shared/svelte/Components/Order/IndividualOrderSummary.svelte";
  import RequestItemsContainer from "@retailer/svelte/Cart/Page/DistributorSection/RequestItemsContainer.svelte";

  // Types
  import type { CartOrderType } from "@shared/types/Cart";
  import SfFields from "@retailer/svelte/Cart/Page/DistributorSection/SFFields.svelte";
  import PickupOrDeliveryBanner from "../PickupOrDelivery/PickupOrDeliveryBanner.svelte";
  import { isOHLQOrder } from "@retailer/services/order_fulfillment_service";
  import { isAccountNumberRequired } from "@retailer/services/product_card_service";

  // Props
  export let order: CartOrderType;
  export let cartService: any; // Angular Service
  export let showLoading: boolean;
  export let customPricingMode: any;
  export let callbacks: any;

  // Data
  let warehouses = [];

  const inFulfillmentEnhancementsRollout = stagedRolloutCheck(
    "marketplace__cart_page_fulfillment_enhancements",
  );

  $: {
    order.products;
    warehouses = splitByWarehouse(order);
  }

  $: showPickupOrDeliveryBanner =
    inFulfillmentEnhancementsRollout &&
    order.distributor?.uses_wine_cellar &&
    order.distributor?.uses_warehouse_pickup &&
    !isOHLQOrder(order.distributor?.id);

  const needsAccountNumber = async () => {
    const response = await isAccountNumberRequired(
      order.distributor?.id,
      order.products[0]?.inventory?.sku,
      order.products[0]?.warehouse_location_code,
      order.distributor?.account_number,
    );
    return response;
  };
</script>

<div transition:slide|local={{ duration: 300, easing: cubicInOut }} data-testid="accordion">
  {#each warehouses as warehouse (warehouse.id)}
    {#if showPickupOrDeliveryBanner}
      {#await needsAccountNumber() then needsAccountNumber}
        {#if !needsAccountNumber}
          <PickupOrDeliveryBanner
            currentLocation={{ id: warehouse.id, name: startCase(toLower(warehouse.name)) }}
          />
        {/if}
      {/await}
    {:else if warehouses.length > 1}
      <div class="fizz-border-top fizz-flex-row fizz-space-between fizz-inset-horizontal-16">
        <p class="fizz-inset-vertical-8 fizz-text-strong">
          {#if warehouse.id != null}In store pickup:
          {/if}{warehouse.name}
        </p>
        <p class="fizz-text-small fizz-inset-vertical-8">
          {getContainerCountString(
            getOrderContainerCounts(filterByWarehouse(order.products, warehouse.id)),
          )}
        </p>
      </div>
    {/if}
    <CartProductsContainer
      {order}
      {warehouse}
      {cartService}
      {callbacks}
      {customPricingMode}
      products={filterByWarehouse(order.products, warehouse.id)}
      currentLocation={{ id: warehouse.id, name: startCase(toLower(warehouse.name)) }}
      bind:showLoading
    />
  {/each}

  {#if !order.products.every(p => p.requested_item)}
    <div class="fizz-surface fizz-border-top" id="order-footer">
      <SfFields {order} {cartService} bind:showLoading />
      <section
        class="fizz-inset-square-16 fizz-align-content-start fizz-flex-row-24-responsive fizz-flex-vertical"
        style="gap: 16px;"
      >
        <IndividualOrderSummary
          bind:total={order.total}
          bind:subtotal={order.subtotal}
          bind:taxTotal={order.tax_total}
          orderProducts={order.order_products}
          products={order.products}
          distributor={order.distributor}
          totalItems={order.total_items}
        />

        {#if order.products.find(p => p.requested_item)}
          <RequestItemsContainer
            {order}
            {cartService}
            {callbacks}
            {showLoading}
            {customPricingMode}
            products={order.products}
          />
        {/if}
      </section>
    </div>
  {/if}
</div>
